import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiService from '../services/api';
import PageBackground from '../components/PageBackground';
import { useLanguage } from '../contexts/LanguageContext';
import './BhaktiYoga.css';

const BhaktiYoga = () => {
  const { t, language } = useLanguage();
  const [catalogues, setCatalogues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const imageUrl = '/images/bg.avif';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const fetchCataloguesWithCourses = async () => {
      try {
        const response = await apiService.getAllPublishedCourses();
        if (response.success) {
          const activeCatalogues = response.data.filter(cat => cat.courses && cat.courses.length > 0);
          setCatalogues(activeCatalogues);
        } else {
          throw new Error(response.message || t('bhaktiYogaPage.errors.loadCourses'));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCataloguesWithCourses();
  }, []);

  useEffect(() => {
    document.body.classList.add('bhakti-yoga-theme');
    return () => {
      document.body.classList.remove('bhakti-yoga-theme');
    };
  }, []);

  const toTranslationKey = (value = '') => value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const getDynamicYogaFallback = (text = '', contentType = 'generic') => {
    const normalized = text.trim().toLowerCase().replace(/\s+/g, ' ');
    const advancedTitleMatch = text.trim().match(/^yoga\s*[–-]\s*fundamental(s)?\s*\((advance|advanced)\)$/i);

    if (language === 'hi') {
      if (contentType === 'catalogueName' && normalized === 'yoga') {
        return 'योग';
      }
      if (
        contentType === 'catalogueDescription' &&
        normalized.startsWith('this category offers a deep and holistic journey into the ancient science of yoga')
      ) {
        return 'यह श्रेणी योग के प्राचीन विज्ञान में गहरी और समग्र यात्रा प्रदान करती है, जिसमें शारीरिक अभ्यास, मानसिक अनुशासन और आध्यात्मिक जागरूकता का सुंदर समन्वय है। संरचित अभ्यास, योग-दर्शन, श्वास-जागरूकता और सजग जीवनशैली के माध्यम से शरीर, मन और आत्मा में सामंजस्य स्थापित करना सीखें। चाहे आप प्रारंभिक साधक हों या उन्नत अभ्यासी, ये पाठ्यक्रम आपको आंतरिक संतुलन, स्पष्टता और दीर्घकालिक कल्याण की दिशा में मार्गदर्शन करते हैं। यह तनाव-निवारण, माइंडफुलनेस, भावनात्मक संतुलन और आत्म-परिवर्तन के लिए आदर्श है।';
      }
      if (contentType === 'courseTitle' && /^yoga\s*[–-]\s*fundamental(s)?$/i.test(text.trim())) {
        return 'योग – मूलभूत';
      }
      if (contentType === 'courseTitle' && advancedTitleMatch) {
        return 'योग – मूलभूत (उन्नत)';
      }
      if (
        contentType === 'courseDescription' &&
        normalized.startsWith('the yoga') &&
        normalized.includes('thoughtfully designed for beginners and wellness seekers')
      ) {
        return 'योग – फंडामेंटल्स पाठ्यक्रम को शुरुआती साधकों और वेलनेस साधकों के लिए सावधानीपूर्वक तैयार किया गया है, जो एक मजबूत, संतुलित और जागरूक योग अभ्यास की नींव बनाना चाहते हैं। यह पाठ्यक्रम आसन, श्वास-प्रक्रिया, एकाग्रता और दैनिक जीवन में योग के व्यावहारिक अनुप्रयोग को सरल और क्रमबद्ध तरीके से सिखाता है।';
      }
      if (
        contentType === 'courseDescription' &&
        /^yoga fundamentals \((advance|advanced)\) provide the stable base needed for safe advancement/.test(normalized)
      ) {
        return 'योग फंडामेंटल्स (उन्नत) पाठ्यक्रम अधिक चुनौतीपूर्ण आसनों की ओर सुरक्षित प्रगति के लिए आवश्यक स्थिर आधार प्रदान करता है, जिसमें सही संरेखण, संतुलन, कोर शक्ति और श्वास-नियंत्रण पर विशेष जोर दिया जाता है।';
      }
    }

    if (language === 'or') {
      if (contentType === 'catalogueName' && normalized === 'yoga') {
        return 'ଯୋଗ';
      }
      if (
        contentType === 'catalogueDescription' &&
        normalized.startsWith('this category offers a deep and holistic journey into the ancient science of yoga')
      ) {
        return 'ଏହି ଶ୍ରେଣୀଟି ଯୋଗର ପ୍ରାଚୀନ ବିଜ୍ଞାନକୁ ଗଭୀର ଏବଂ ସମଗ୍ର ଭାବରେ ଅନୁଭବ କରାଏ, ଯେଉଁଠାରେ ଶାରୀରିକ ଅଭ୍ୟାସ, ମାନସିକ ଶିଷ୍ଟାଚାର ଏବଂ ଆଧ୍ୟାତ୍ମିକ ସଚେତନତାର ସମନ୍ୱୟ ରହିଛି। ଗଠିତ ଅଭ୍ୟାସ, ଦର୍ଶନ, ଶ୍ୱାସ-ସଚେତନତା ଏବଂ ସଚେତ ଜୀବନଶୈଳୀ ମାଧ୍ୟମରେ ଶରୀର, ମନ ଓ ଆତ୍ମାର ସମନ୍ୱୟ କିପରି କରିବେ ତାହା ଶିଖନ୍ତୁ। ଆପଣ ଆରମ୍ଭିକ ସାଧକ ହେଉନ୍ତୁ କିମ୍ବା ଉନ୍ନତ ଅଭ୍ୟାସୀ, ଏହି ପାଠ୍ୟକ୍ରମମାନେ ଆପଣଙ୍କୁ ଅନ୍ତର୍ନିହିତ ସନ୍ତୁଳନ, ସ୍ପଷ୍ଟତା ଏବଂ ଦୀର୍ଘକାଳୀନ ସୁସ୍ଥତା ଦିଗରେ ନେଇଯାଏ। ଏହା ଚାପ ହ୍ରାସ, ମାଇଣ୍ଡଫୁଲନେସ୍, ଭାବନାତ୍ମକ ସନ୍ତୁଳନ ଏବଂ ଆତ୍ମ-ପରିବର୍ତ୍ତନ ପାଇଁ ଅତ୍ୟନ୍ତ ଉପଯୋଗୀ।';
      }
      if (contentType === 'courseTitle' && /^yoga\s*[–-]\s*fundamental(s)?$/i.test(text.trim())) {
        return 'ଯୋଗ – ମୂଳଭିତ୍ତି';
      }
      if (contentType === 'courseTitle' && advancedTitleMatch) {
        return 'ଯୋଗ – ମୂଳଭିତ୍ତି (ଉନ୍ନତ)';
      }
      if (
        contentType === 'courseDescription' &&
        normalized.startsWith('the yoga') &&
        normalized.includes('thoughtfully designed for beginners and wellness seekers')
      ) {
        return 'ଯୋଗ – ଫଣ୍ଡାମେଣ୍ଟାଲ୍ସ ପାଠ୍ୟକ୍ରମଟି ଆରମ୍ଭିକ ଅଭ୍ୟାସୀ ଓ ସୁସ୍ଥତାକାମୀ ଲୋକଙ୍କ ପାଇଁ ଭାବିଚିନ୍ତିତ ଭାବରେ ପ୍ରସ୍ତୁତ, ଯେଉଁମାନେ ଦୃଢ଼, ସନ୍ତୁଳିତ ଓ ସଚେତନ ଯୋଗ ଅଭ୍ୟାସର ଭିତ୍ତି ତିଆରି କରିବାକୁ ଚାହାନ୍ତି। ଏହି ପାଠ୍ୟକ୍ରମରେ ଆସନ, ଶ୍ୱାସାଭ୍ୟାସ, ଏକାଗ୍ରତା ଏବଂ ଦୈନିକ ଜୀବନରେ ଯୋଗର ପ୍ରାୟୋଗିକ ପ୍ରୟୋଗକୁ ସହଜ ଏବଂ କ୍ରମବଦ୍ଧ ଭାବରେ ଶିଖାଯାଏ।';
      }
      if (
        contentType === 'courseDescription' &&
        /^yoga fundamentals \((advance|advanced)\) provide the stable base needed for safe advancement/.test(normalized)
      ) {
        return 'ଯୋଗ ଫଣ୍ଡାମେଣ୍ଟାଲ୍ସ (ଉନ୍ନତ) ପାଠ୍ୟକ୍ରମଟି ଅଧିକ ଚ୍ୟାଲେଞ୍ଜିଂ ଆସନକୁ ସୁରକ୍ଷିତ ଭାବେ ଆଗେଇ ନେବା ପାଇଁ ଆବଶ୍ୟକ ସ୍ଥିର ଭିତ୍ତି ପ୍ରଦାନ କରେ, ଯେଉଁଠାରେ ସଠିକ୍ ସମରେଖଣ, ସନ୍ତୁଳନ, କୋର୍ ଶକ୍ତି ଏବଂ ଶ୍ୱାସ ନିୟନ୍ତ୍ରଣ ଉପରେ ବିଶେଷ ଗୁରୁତ୍ୱ ଦିଆଯାଏ।';
      }
    }

    return text;
  };

  const getTranslatedLevel = (level) => (
    t(`bhaktiYogaPage.levels.${toTranslationKey(level)}`, {}, level)
  );

  const getTranslatedTier = (tier) => (
    t(`bhaktiYogaPage.tiers.${toTranslationKey(tier)}`, {}, tier)
  );

  const getTranslatedCatalogueName = (name) => (
    (() => {
      const translated = t(`bhaktiYogaPage.catalogues.${toTranslationKey(name)}.name`, {}, name);
      return translated !== name ? translated : getDynamicYogaFallback(name, 'catalogueName');
    })()
  );

  const getTranslatedCatalogueDescription = (description) => (
    (() => {
      const translated = t(`bhaktiYogaPage.catalogues.${toTranslationKey(description)}.description`, {}, description);
      return translated !== description ? translated : getDynamicYogaFallback(description, 'catalogueDescription');
    })()
  );

  const getTranslatedCourseTitle = (title) => (
    (() => {
      const translated = t(`bhaktiYogaPage.courses.${toTranslationKey(title)}.title`, {}, title);
      return translated !== title ? translated : getDynamicYogaFallback(title, 'courseTitle');
    })()
  );

  const getTranslatedCourseDescription = (description) => (
    (() => {
      const translated = t(`bhaktiYogaPage.courses.${toTranslationKey(description)}.description`, {}, description);
      return translated !== description ? translated : getDynamicYogaFallback(description, 'courseDescription');
    })()
  );

  const getTierClassName = (tier) => `tier-badge tier-${tier.toLowerCase()}`;

  return (
    <div className="bhakti-yoga-page">
      <PageBackground imageUrl={imageUrl} />
      
      <div className="yoga-page-header">
        <div className="container">
          <h1>{t('bhaktiYogaPage.header.title')}</h1>
          <p>{t('bhaktiYogaPage.header.subtitle')}</p>
        </div>
      </div>

      <div className="yoga-main-content">
        <div className="container">
          {isLoading ? (
            <div className="loading-spinner">{t('bhaktiYogaPage.loadingCourses')}</div>
          ) : error ? (
            <div className="error-message">{t('bhaktiYogaPage.errorPrefix')}: {error}</div>
          ) : (
            <div className="catalogues-container">
              {catalogues.length > 0 ? (
                catalogues.map(catalogue => (
                  <div key={catalogue._id} className="catalogue-section">
                    <div className="catalogue-header">
                      <h2 className="catalogue-title">{getTranslatedCatalogueName(catalogue.name)}</h2>
                      <p className="catalogue-description">{getTranslatedCatalogueDescription(catalogue.description)}</p>
                    </div>
                    <div className="course-grid">
                      {catalogue.courses.map((course) => {
                        const translatedCourseTitle = getTranslatedCourseTitle(course.title);
                        const translatedCourseDescription = getTranslatedCourseDescription(course.description);

                        return (
                          <motion.div
                            key={course._id}
                            className="course-card"
                            whileHover={{ y: -8 }}
                            transition={{ type: 'spring', stiffness: 300 }}
                          >
                            <Link to={`/bhakti-yoga/courses/${course._id}`} className="card-link-wrapper">

                              {/* --- 1. THUMBNAIL --- */}
                              <div className="card-thumbnail">
                                <img src={course.thumbnail} alt={translatedCourseTitle} />
                              </div>

                              {/* --- 2. TEXT CONTENT WRAPPER --- */}
                              <div className="card-text-content">
                                <div className="card-header">
                                  <p className="course-level">{getTranslatedLevel(course.level)}</p>
                                  <span className={getTierClassName(course.subscriptionTier)}>
                                    {getTranslatedTier(course.subscriptionTier)}
                                  </span>
                                </div>
                                <h3>{translatedCourseTitle}</h3>
                                <p className="course-description">
                                  {translatedCourseDescription.length > 120
                                    ? `${translatedCourseDescription.substring(0, 120)}...`
                                    : translatedCourseDescription}
                                </p>
                              </div>

                            </Link>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-results-message">{t('bhaktiYogaPage.noCoursesMessage')}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BhaktiYoga;
